/**
 * 스테이지 매핑
 *
 * 서버의 JobStagesResponse(outline 5단계 + 로그)를 StageView 조직(organism)이
 * 기대하는 StageColumn/StageRun 형태로 변환하는 순수 함수 모음.
 * - 열(column) = outline 상위 5단계 (label)
 * - 행(run)   = Job 1건, 각 셀 = 해당 단계 노드 status
 * API는 단계별 소요시간을 주지 않으므로 durationLabel/avgLabel은 비움.
 */

import type {
  StageCell,
  StageCellStatus,
  StageColumnGroup,
  StageLeaf,
  StageRun,
} from '../organisms/StageView/StageView';
import type {
  JobStagesResponse,
  StageNode,
  StageNodeStatus,
} from '../api/types';
import type { JobStatus } from '../types';

/** 스테이지 매트릭스가 필요로 하는 Job 최소 정보. JobRow/JobDetailData 공통. */
export interface StageJobRef {
  id: number;
  module: string;
  status: string;
  created: string;
}

/** 서버 노드 상태 → StageView 셀 상태. */
export function stageCellStatus(status: StageNodeStatus): StageCellStatus {
  switch (status) {
    case 'DONE':
    case 'CACHED':
      return 'success';
    case 'FAILED':
      return 'failed';
    case 'RUNNING':
      return 'running';
    case 'SKIPPED':
      return 'skipped';
    case 'PENDING':
    default:
      return 'empty';
  }
}

/** 생성시각 ISO → 로컬 "MM-DD HH:mm". 파싱 실패 시 원본. */
function formatRunDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** 노드 라벨 (한글 라벨 없으면 코드). */
function nodeName(node: StageNode): string {
  return node.label || node.stage;
}

/** 종료(성공 계열) 상태 여부. */
function isTerminal(status: StageNodeStatus): boolean {
  return status === 'DONE' || status === 'CACHED' || status === 'SKIPPED';
}

/**
 * 폴링된 outline 상위 단계 상태 → Job 전체 상태 도출.
 * 서버 job.status는 조회 시점 스냅샷이라, 30초 폴링으로 갱신되는
 * 단계 상태에서 실시간 상태를 다시 계산해 배지/타임라인에 반영한다.
 * - 하나라도 FAILED → FAILED
 * - 하나라도 RUNNING → RUNNING
 * - 전부 종료(DONE/CACHED/SKIPPED) → SUCCESS
 * - 일부만 종료(나머지 PENDING) → RUNNING(진행 중), 전부 PENDING → QUEUED
 */
export function deriveJobStatus(res: JobStagesResponse): JobStatus {
  const nodes = res.outline;
  if (nodes.length === 0) return 'QUEUED';
  if (nodes.some((n) => n.status === 'FAILED')) return 'FAILED';
  if (nodes.some((n) => n.status === 'RUNNING')) return 'RUNNING';
  if (nodes.every((n) => isTerminal(n.status))) return 'SUCCESS';
  if (nodes.some((n) => isTerminal(n.status))) return 'RUNNING';
  return 'QUEUED';
}

/**
 * 더 이상 상태가 바뀌지 않는 종결 상태(성공/실패) 여부.
 * StageView 폴링을 언제 멈출지 판단하는 데 쓴다 — 끝난 Job을 계속 조회할 필요가 없다.
 */
export function isJobSettled(status: JobStatus): boolean {
  return status === 'SUCCESS' || status === 'FAILED';
}

/** 노드 → StageView 셀. 진행 중이면 안내 문구. */
function toCell(status: StageNodeStatus, detail: string | null): StageCell {
  const cellStatus = stageCellStatus(status);
  return {
    status: cellStatus,
    note: cellStatus === 'running' ? '진행 중…' : detail ?? undefined,
  };
}

/**
 * outline 단계 → StageView 열 그룹.
 * 자식이 있으면 leaves로 묶고, 없으면 leaves=[]라 단일 열로 렌더된다.
 */
export function toStageColumns(res: JobStagesResponse): StageColumnGroup[] {
  return res.outline.map((node) => ({
    name: nodeName(node),
    no: node.no,
    leaves: node.children.map((child) => ({
      name: nodeName(child),
      no: child.no,
    })),
  }));
}

/** 리프 열의 정렬 키 = 단계 번호(no). 없으면 이름으로 폴백. */
function leafKey(leaf: StageLeaf): string {
  return leaf.no ?? leaf.name;
}

/**
 * 컬럼 그룹 목록 → 리프 열의 no 순서.
 * 자식이 있으면 자식들의 no, 없으면 그룹 자신의 no. StageView의 leafColumns와 동일 순서.
 */
export function leafNosOf(columns: StageColumnGroup[]): string[] {
  return columns.flatMap((g) =>
    g.leaves.length > 0 ? g.leaves.map(leafKey) : [g.no ?? g.name]
  );
}

/**
 * outline → 단계 번호(no)별 셀 맵.
 * 자식이 PENDING인데 부모가 FAILED/SKIPPED로 끝났으면 그 상태를 반영(실패 신호 유지).
 */
export function toStageCellMap(res: JobStagesResponse): Map<string, StageCell> {
  const map = new Map<string, StageCell>();
  for (const node of res.outline) {
    if (node.children.length > 0) {
      for (const child of node.children) {
        const status =
          child.status === 'PENDING' &&
          (node.status === 'FAILED' || node.status === 'SKIPPED')
            ? node.status
            : child.status;
        map.set(child.no, toCell(status, child.detail));
      }
    } else {
      map.set(node.no, toCell(node.status, node.detail));
    }
  }
  return map;
}

/**
 * 여러 Job의 컬럼을 no 기준으로 합집합.
 * collector마다 COLLECT 자식 구성이 달라(csv-parser 4개 vs node-scraper 3개)
 * 첫 Job의 컬럼만 쓰면 셀이 인덱스로 밀린다. 등장 순서를 유지하며 no로 병합해
 * 어떤 collector의 단계도 빠지지 않게 한다. 라벨은 처음 본 것을 사용.
 */
export function mergeStageColumns(perJob: StageColumnGroup[][]): StageColumnGroup[] {
  const order: string[] = [];
  const groups = new Map<
    string,
    { name: string; no?: string; leafOrder: string[]; leaves: Map<string, StageLeaf> }
  >();
  for (const cols of perJob) {
    for (const g of cols) {
      const gk = g.no ?? g.name;
      let entry = groups.get(gk);
      if (!entry) {
        entry = { name: g.name, no: g.no, leafOrder: [], leaves: new Map() };
        groups.set(gk, entry);
        order.push(gk);
      }
      for (const leaf of g.leaves) {
        const lk = leafKey(leaf);
        if (!entry.leaves.has(lk)) {
          entry.leaves.set(lk, leaf);
          entry.leafOrder.push(lk);
        }
      }
    }
  }
  return order.map((gk) => {
    const e = groups.get(gk)!;
    return { name: e.name, no: e.no, leaves: e.leafOrder.map((lk) => e.leaves.get(lk)!) };
  });
}

/** leafNos에서 from 다음으로 이 Job에 실재하는 셀을 찾는다(없으면 undefined). */
function nextPresentCell(
  leafNos: string[],
  from: number,
  cellMap: Map<string, StageCell>
): StageCell | undefined {
  for (let i = from + 1; i < leafNos.length; i += 1) {
    const c = cellMap.get(leafNos[i]);
    if (c) return c;
  }
  return undefined;
}

/**
 * Job 1건 → StageView 행. 셀은 leafNos(공유 컬럼의 no 순서)에 맞춰 배치한다.
 * 인덱스가 아니라 no로 정렬하므로 collector가 섞여도(자식 수가 달라도) 칸이 밀리지 않는다.
 * 이 Job에 없는 단계(다른 collector에만 있는 열, 예: node-scraper의 1-4)는 기본 빈 셀이되,
 * 파이프라인이 그 지점을 지나 다음 실재 단계가 성공이면 성공색으로 이어 칠한다.
 */
export function toStageRun(
  res: JobStagesResponse,
  job: StageJobRef,
  leafNos: string[]
): StageRun {
  const cellMap = toStageCellMap(res);
  const cells: StageCell[] = leafNos.map((no, i) => {
    const cell = cellMap.get(no);
    if (cell) return cell;
    // 이 collector에 없는 단계: 다음 실재 단계가 성공이면 회색 구멍 대신 성공색.
    const next = nextPresentCell(leafNos, i, cellMap);
    if (next && next.status === 'success') return { status: 'success' as const };
    return { status: 'empty' as const };
  });
  return {
    id: job.id,
    dateLabel: formatRunDate(job.created),
    // {모듈} (수집기 타입). collector는 stages 응답에서 제공.
    changes: res.collector ? `${job.module} (${res.collector})` : job.module,
    // 셀과 동일하게 폴링된 outline 기준으로 실패 강조 (정적 job.status 대신).
    failed: deriveJobStatus(res) === 'FAILED',
    cells,
  };
}
